/* eslint-disable max-len */
import { useEffect, useMemo, useState } from 'react';
import './style.scss';
import UploadedList from './components/UploadedList';
import { useStore } from '../../core/utils/hook';
import { formatBytes } from '../../core/utils/common';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconDocument, IconVideo } from './components/Icon';
import ProfileLayout from '../Profile/components/ProfileLayout';
import { SYSTEM_PATH } from '../../core/configs/constants';
import { canShowShareKnowledgeMenu } from '../../core/utils/gaMenuPermissions';

export const TAB_ID = {
    VIDEO: 0,
    DOCUMENT: 1
};

const ShareKnowledgeScreen = () => {
    const navigate = useNavigate();

    // store
    const {
        userStore: { userInfoHeader },
        shareKnowledgeStore: { getGroupInfo, groupInfo }
    } = useStore();

    useEffect(() => {
        if (Object.keys(userInfoHeader).length > 0 && !canShowShareKnowledgeMenu(userInfoHeader)) {
            navigate(SYSTEM_PATH.HOME, { replace: true });
        }
    }, [userInfoHeader]);
    const [searchParams, setSearchParams] = useSearchParams();

    const [activeTab, setActiveTab] = useState(null);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        const tabName = tabId === TAB_ID.DOCUMENT ? 'document' : 'video';
        setSearchParams({ tab: tabName });
    };

    useEffect(() => {
        getGroupInfo();
    }, [userInfoHeader]);

    const tabs = useMemo(
        () => [
            {
                id: TAB_ID.VIDEO,
                label: '動画',
                content: <UploadedList type={TAB_ID.VIDEO} />,
                icon: '/images/u266.svg',
                disabled: !+groupInfo?.videoMaximumCapacity
            },
            {
                id: TAB_ID.DOCUMENT,
                label: 'ドキュメント',
                content: <UploadedList type={TAB_ID.DOCUMENT} />,
                icon: '/images/u267.svg',
                disabled: !+groupInfo?.documentMaximumCapacity
            }
        ],
        [activeTab, groupInfo]
    );

    useEffect(() => {
        if (Object.keys(groupInfo).length === 0) return;
        const tabParam = searchParams.get('tab');

        const isVideoAvailable = +groupInfo?.videoMaximumCapacity;
        const isDocAvailable = +groupInfo?.documentMaximumCapacity;

        if (tabParam === 'video' && isVideoAvailable) {
            setActiveTab(TAB_ID.VIDEO);
        } else if (tabParam === 'document' && isDocAvailable) {
            setActiveTab(TAB_ID.DOCUMENT);
        } else if (!tabParam) {
            if (isVideoAvailable) {
                setActiveTab(TAB_ID.VIDEO);
            } else if (isDocAvailable) {
                setActiveTab(TAB_ID.DOCUMENT);
            } else {
                setActiveTab(null);
            }
        } else {
            // Unknown tab or requested tab not available — fall back to video, then doc, then null
            if (isVideoAvailable) {
                setActiveTab(TAB_ID.VIDEO);
            } else if (isDocAvailable) {
                setActiveTab(TAB_ID.DOCUMENT);
            } else {
                setActiveTab(null);
            }
        }
    }, [groupInfo, searchParams]);

    return (
        // This function is still under development
        <ProfileLayout titlePage={'シェアナレッジ'} type="test-history-screen">
            <div className="share-knowledge-screen">
                {Object.keys(groupInfo).length === 0 ? (
                    <div className="no-plan-container">
                        <div className="flex justify-center">
                            <div className="icon-group">
                                <img src="/images/iconLock.png" style={{ height: '120px' }} />
                            </div>
                        </div>
                        <div className="content">
                            <p
                                style={{
                                    fontWeight: '600',
                                    fontSize: '16px',
                                    maxWidth: '800px',
                                    margin: '0 auto',
                                    textAlign: 'center'
                                }}
                                className="text-black"
                            >
                                サービスが設定されていません。管理者にお問い合わせください。
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="data-usage-container">
                            <div className="flex group-info">
                                {/* Video Data Section */}
                                <div className="data-section video-section">
                                    <div className="flex justify-start gap-10 mg-b-30">
                                        <div className="icon-container">{IconVideo()}</div>
                                        <div>
                                            <p className="type">共有動画数</p>
                                            <p className="count">
                                                {+groupInfo?.totalVideo ? groupInfo?.totalVideo : '--'}
                                            </p>
                                        </div>
                                    </div>

                                    {+groupInfo?.videoMaximumCapacity ? (
                                        <>
                                            <div
                                                className="progress-bar"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="bottom"
                                                title={`利用可能な空き容量：${formatBytes(
                                                    +groupInfo?.videoMaximumCapacity - +groupInfo?.totalVideoSize
                                                )}`}
                                            >
                                                <div
                                                    className="progress video-progress"
                                                    style={{
                                                        width: `${
                                                            (+groupInfo?.totalVideoSize /
                                                                +groupInfo?.videoMaximumCapacity) *
                                                            100
                                                        }%`
                                                    }}
                                                ></div>
                                            </div>
                                            <p className="usage-info text-13">
                                                <span
                                                    style={{
                                                        color: '#F6A000',
                                                        fontSize: '16px'
                                                    }}
                                                >
                                                    {formatBytes(+groupInfo?.totalVideoSize)}{' '}
                                                </span>
                                                <span style={{ color: '#F6A000', fontSize: '16px' }}>使用済み</span> /{' '}
                                                {formatBytes(+groupInfo?.videoMaximumCapacity)}
                                            </p>
                                        </>
                                    ) : (
                                        <div className="text-13 text-red-primary">
                                            <p>現在のご受講プランでは、動画の共有機能はご利用いただけません。<br/>本機能を有効化するには、Elephamtの管理者までお問い合わせください。</p>
                                        </div>
                                    )}
                                </div>
                                {/* File Data Section */}
                                <div className="data-section document-section">
                                    <div className="flex justify-start gap-10 mg-b-30">
                                        <div className="icon-container">{IconDocument()}</div>
                                        <div>
                                            <p className="type">共有ドキュメント数</p>
                                            <p className="count">
                                                {+groupInfo?.totalDocument ? groupInfo?.totalDocument : '--'}
                                            </p>
                                        </div>
                                    </div>

                                    {+groupInfo?.documentMaximumCapacity ? (
                                        <>
                                            <div
                                                className="progress-bar"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="bottom"
                                                title={`利用可能な空き容量：${formatBytes(
                                                    +groupInfo?.documentMaximumCapacity - +groupInfo?.totalDocumentSize
                                                )}`}
                                            >
                                                <div
                                                    className="progress document-progress"
                                                    style={{
                                                        width: `${
                                                            (+groupInfo?.totalDocumentSize /
                                                                +groupInfo?.documentMaximumCapacity) *
                                                            100
                                                        }%`
                                                    }}
                                                ></div>
                                            </div>
                                            <p className="usage-info text-13">
                                                <span
                                                    style={{
                                                        color: '#027DB4',
                                                        fontSize: '16px'
                                                    }}
                                                >
                                                    {formatBytes(+groupInfo?.totalDocumentSize)}{' '}
                                                </span>
                                                <span style={{ color: '#027DB4', fontSize: '16px' }}>使用済み</span> /{' '}
                                                {formatBytes(+groupInfo?.documentMaximumCapacity)}
                                            </p>
                                        </>
                                    ) : (
                                        <div className="text-13 text-red-primary">
                                            <p>現在の受講プランでは、ドキュメントの共有機能はご利用いただけません。<br/>本機能を有効化するには、Elephamtの管理者までお問い合わせください。</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="uploaded-file-container">
                            <h3 className="title">アップロードされたファイル</h3>
                            <div className="tab-container">
                                <div className="tab-header flex item-center justify-between wrap">
                                    {/* Tabs */}
                                    <div className="tabs">
                                        {tabs.map((tab) => (
                                            <div
                                                key={tab.id}
                                                className={`tab ${activeTab === tab.id ? 'active' : ''} ${
                                                    tab.disabled ? 'disabled' : ''
                                                }`}
                                                onClick={() => !tab.disabled && handleTabChange(tab.id)}
                                            >
                                                {tab.label}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* Tab content */}
                                <div className="tab-content">
                                    {tabs.map((tab) => (
                                        <div
                                            key={tab.id}
                                            style={{
                                                display: activeTab === tab.id ? 'block' : 'none'
                                            }}
                                            className="content"
                                        >
                                            {tab.content}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </ProfileLayout>
    );
};

export default ShareKnowledgeScreen;
